/**
 * Common validation utilities for WPS forms
 * Provides reusable validation functions for General.jsx, WeldingProcedure.jsx, and WeldingProcedureSpec.jsx
 */

// Common validation messages
export const VALIDATION_MESSAGES = {
  REQUIRED: "This field is required",
  MANDATORY_PROCESS:
    "1) At least one field of each process is Mandatory.\n2) No Special Characters other than space, comma, +, -, colon, semicolon, dot, @, #, $, &, (), *, !, _, / are allowed.",
  SPECIAL_CHARS: "Special Characters not allowed",
  FUTURE_DATE: "No Future Date Allowed.",
};

/**
 * Validates a single field value against regex pattern
 * @param {string} value - Field value to validate
 * @param {RegExp} regExp - Regular expression pattern
 * @param {string} customMessage - Custom error message (optional)
 * @returns {Object} Validation result with status and message
 */
export const validateFieldValue = (
  value,
  customMessage = VALIDATION_MESSAGES
) => {
  if (
    !value ||
    (typeof value === "string" && value.trim() === "") ||
    value === ""
  ) {
    return { status: true, message: customMessage.REQUIRED };
  }

  return { status: false, message: "" };
};

/**
 * Validates array field values (for process arrays)
 * @param {Array} values - Array of values to validate
 * @param {RegExp} regExp - Regular expression pattern
 * @param {string} customMessage - Custom error message (optional)
 * @returns {Object} Validation result with status and message
 */
export const validateArrayField = (
  values,
  customMessage = VALIDATION_MESSAGES
) => {
  const hasValue = values.some(
    (val) => val !== undefined && val !== "" && val !== null
  );

  if (!hasValue) {
    return { status: true, message: customMessage.REQUIRED };
  }
  return { status: false, message: "" };
};

/**
 * Validates required fields from a configuration object
 * @param {Object} dataObj - Object containing field values
 * @param {Object} fieldsConfig - Configuration object defining required fields
 * @param {Object} currentErrors - Current error state
 * @returns {Object} Updated error object and isError flag
 */
export const validateRequiredFields = (
  dataObj,
  fieldsConfig,
  currentErrors = {}
) => {
  const errors = { ...currentErrors };
  let isError = false;

  Object.entries(fieldsConfig).forEach(([key]) => {
    if (dataObj[key] === "" || dataObj[key] === undefined) {
      isError = true;
      errors[key] = {
        status: true,
        message: VALIDATION_MESSAGES.REQUIRED,
      };
    }
  });

  return { errors, isError };
};

/**
 * Validates detail object arrays (for WPS/PQR process validation)
 * @param {Array|Object} detailObj - Array of detail objects or single object
 * @param {Array} errorConfig - Error configuration array
 * @param {Object} currentErrors - Current error state
 * @param {Function} setError - Error setter function for PQR compatibility
 * @returns {Object} Updated error object and isError flag
 */
export const validateDetailArrays = (
  detailObj,
  errorConfig,
  currentErrors = {},
  setError = null
) => {
  const errors = { ...currentErrors };
  let isError = false;

  errorConfig.forEach((cols) => {
    // Handle both WPS (array) and PQR (object) patterns
    console.log("isArray", Array.isArray(detailObj))
    const entries = Array.isArray(detailObj)
      ? Object.entries(detailObj)
      : [["0", detailObj]];


    entries.forEach(([key]) => {
      if (cols.Type === "S") {
        const value = Array.isArray(detailObj)
          ? detailObj[key][cols.Column]
          : detailObj[cols.Column];
        console.log("isArray", Array.isArray(detailObj), value, cols.Column, detailObj[cols.Column], detailObj)
        const validation = validateFieldValue(value);
        if (validation.status) {
          isError = true;
          errors[cols.Column] = validation;

          // PQR compatibility - use setError if provided
          if (setError) {
            setError((prevState) => ({
              ...prevState,
              [cols.Column]: validation,
            }));
          }
        }
      } else if (cols.Type === "SA") {
        // WPQ-specific SA type validation
        const actualValue = Array.isArray(detailObj)
          ? detailObj[key][cols.Column]?.[0]?.Actual_Value?.[0]
          : detailObj[cols.Column]?.[0]?.Actual_Value?.[0];

        const rangeValue = Array.isArray(detailObj)
          ? detailObj[key][cols.Column]?.[1]?.Range_Qualified?.[0]
            ?.Range_Qualified1?.[0]
          : detailObj[cols.Column]?.[1]?.Range_Qualified?.[0]
            ?.Range_Qualified1?.[0];

        if (
          !actualValue ||
          actualValue === "" ||
          !rangeValue ||
          rangeValue === ""
        ) {
          isError = true;
          const validation = {
            status: true,
            message: VALIDATION_MESSAGES.REQUIRED,
          };
          errors[cols.Column] = validation;

          if (setError) {
            setError((prevState) => ({
              ...prevState,
              [cols.Column]: validation,
            }));
          }
        }
      } else if (cols.Type === "A") {
        const values = Array.isArray(detailObj)
          ? [
            detailObj[key]?.[cols.Column]?.[0],
            detailObj[key]?.[cols.Column]?.[1],
            detailObj[key]?.[cols.Column]?.[2],
          ]
          : [
            detailObj[cols.Column]?.[0],
            detailObj[cols.Column]?.[1],
            detailObj[cols.Column]?.[2],
          ];

        const validation = validateArrayField(values);
        if (validation.status) {
          isError = true;
        }
        errors[cols.Column] = validation;
        // PQR compatibility - use setError if provided
        if (setError) {
          setError((prevState) => ({
            ...prevState,
            [cols.Column]: validation,
          }));
        }
      }
    });
  });

  return { errors, isError };
};

/**
 * Common process validation - checks if at least one process is added
 * @param {Array} detailObj - Detail array to validate
 * @param {Object} config - Configuration object
 * @returns {boolean} True if no valid processes found
 */
export const checkProcessAdded = (detailObj, config) => {
  const {
    processField,
    typeField,
    fNoField,
    weldLayerField,
    SFAfield,
    wpsType,
    maxRows,
  } = config;
  let norows = true;
  let isArray = Array.isArray(detailObj)
  if (isArray) {
    for (let i = 0; i < maxRows; i++) {
      if (!detailObj[i]) continue;

      detailObj[i].errormessage = "";
      detailObj[i].error = false;

      if (
        detailObj[i][processField] !== "" &&
        detailObj[i][processField] !== "NA"
      ) {
        norows = false;
        if (fNoField && detailObj[i][fNoField] === "") {
          detailObj[i].errormessage = "FNo is required";
          detailObj[i].error = true;
          norows = true; // Prevent saving when FNo is missing
        }
        console.log("Type Field",typeField)
        if (typeField && detailObj[i][typeField] === "") {
          detailObj[i].errormessage = "Type is required";
          detailObj[i].error = true;
          norows = true; // Set norows = true for Type (matches old project)
        }

        if (
          wpsType === "Overlay" &&
          weldLayerField &&
          detailObj[i][weldLayerField] === ""
        ) {
          detailObj[i].errormessage = "Weld Layer is required";
          detailObj[i].error = true;
          norows = true; // Prevent saving when Weld Layer is missing
        }

        // SFA validation only for Tubesheet type (matches old system)
        if (
          SFAfield && 
          wpsType === "Tubesheet" &&
          detailObj[i][SFAfield] === ""
        ) {
          detailObj[i].errormessage = "SFA is required";
          detailObj[i].error = true;
          norows = true;
        }
      }
    }
  }
  else {
    if (
      detailObj[processField] !== "" &&
      detailObj[processField] !== "NA"
    ) {
      norows = false;
      // SFA validation only for Tubesheet type (matches old system)
      if (
        SFAfield && 
        wpsType === "Tubesheet" &&
        detailObj[SFAfield] === ""
      ) {
        detailObj.errormessage = "SFA is required";
        detailObj.error = true;
        norows = true;
      }
    }
  }
  return norows;
};

/**
 * Generic Test Array Validator - validates test arrays with configurable fields
 * @param {Array} testArray - Array of test objects to validate
 * @param {Array} requiredFields - Array of field names that must be filled
 * @param {number} maxRows - Maximum number of rows to validate
 * @param {Object} options - Additional options
 * @returns {number|boolean} Number of valid rows or boolean for error state
 */
export const validateTestArray = (
  testArray,
  requiredFields,
  maxRows,
  options = {}
) => {
  const {
    requireFirstRow = true,
    errorMessage = "All fields are required",
    returnCount = false,
  } = options;

  let validRows = 0;
  let hasError = false;

  for (let i = 0; i < maxRows; i++) {
    if (!testArray[i]) continue;

    testArray[i].errormessage = "";
    testArray[i].error = false;

    const hasAnyData = requiredFields.some(
      (field) => testArray[i][field] && testArray[i][field] !== ""
    );

    if (i === 0 && !hasAnyData && requireFirstRow) {
      testArray[i].errormessage = "At least one full row required";
      testArray[i].error = true;
      hasError = true;
      continue;
    }

    if (hasAnyData) {
      const missingFields = requiredFields.filter(
        (field) => !testArray[i][field] || testArray[i][field] === ""
      );

      if (missingFields.length > 0) {
        testArray[i].errormessage =
          i === 0 ? "All Fields Are Required" : errorMessage;
        testArray[i].error = true;
        hasError = true;
        if (!returnCount) return true;
      } else {
        validRows++;
      }
    }
  }

  return returnCount ? validRows : hasError;
};

/**
 * Common duplicate process checker - validates for duplicate process combinations
 * @param {Array} detailObj - Array of detail objects to check
 * @param {Object} config - Configuration object
 * @returns {boolean} True if duplicates found
 */
export const checkDuplicateProcess = (detailObj, config) => {
  const { processField, typeField, additionalField } = config;

  let isError = false;

  const combinationKey = additionalField
    ? (item) =>
      `${item[processField]}#${item[typeField]}#${item[additionalField]}`
    : (item) => `${item[processField]}#${item[typeField]}`;

  const unique = detailObj.filter(
    (obj, index) =>
      detailObj.findIndex(
        (item) => combinationKey(item) === combinationKey(obj)
      ) !== index
  );

  if (
    unique.length >= 1 &&
    unique[0][processField] !== "" &&
    unique[0][processField] !== "NA"
  ) {
    const duplicateMessage = additionalField
      ? `Process '${unique[0][processField]}' and ${additionalField} '${unique[0][additionalField]}' is Duplicate`
      : `Process '${unique[0][processField]}' and Type '${unique[0][typeField]}' is Duplicate`;

    detailObj[0].errormessage = duplicateMessage;
    isError = true;
    detailObj[0].error = true;
  } else {
    if (!detailObj[0].error) {
      detailObj[0].errormessage = "";
      detailObj[0].error = false;
    }
  }

  return isError;
};

/**
 * WPS-specific validation for flux requirements
 * @param {Object} headerObj - Header object
 * @param {Array} detailObj - Detail array
 * @returns {Object} Validation result
 */
export const validateWPSFlux = (headerObj, detailObj) => {
  if (
    headerObj.WPSH_fluxRadio !== "None" &&
    !(
      detailObj[0].WPSD_Flux_Details_WI_CHECK ||
      detailObj[1].WPSD_Flux_Details_WI_CHECK ||
      detailObj[2].WPSD_Flux_Details_WI_CHECK
    )
  ) {
    return {
      isError: true,
      error: {
        WPSH_Flux_Details_WISLIP_CHECK: {
          status: true,
          message: "Required fields are not filled.",
        },
      },
    };
  }
  return { isError: false, error: {} };
};

/**
 * WPS-specific validation for oscillation fields (Overlay type)
 */
export const validateWPSOscillation = (headerObj) => {
  let errors = {};
  let isError = false;

  if (headerObj.WPSH_Tech_Oscillation === "Yes") {
    if (!headerObj.WPSH_Tech_Oscillation_Width || headerObj.WPSH_Tech_Oscillation_Width === "") {
      isError = true;
      errors.WPSH_Tech_Oscillation_Width = {
        status: true,
        message: "Oscillation Width is required when Oscillation is Yes",
      };
    }
    if (!headerObj.WPSH_Tech_Oscillation_Freq || headerObj.WPSH_Tech_Oscillation_Freq === "") {
      isError = true;
      errors.WPSH_Tech_Oscillation_Freq = {
        status: true,
        message: "Oscillation Frequency is required when Oscillation is Yes",
      };
    }
  }

  return { isError, error: errors };
};

/**
 * WPS-specific validation for signature fields when approving
 */
export const validateWPSSignatures = (headerObj, approvalStatus) => {
  let errors = {};
  let isError = false;

  // Only validate signatures when trying to approve (not for pending or temp save)
  if (approvalStatus === "Approved") {
    if (!headerObj.WPSH_PreparedBy_Name || headerObj.WPSH_PreparedBy_Name === "") {
      isError = true;
      errors.WPSH_PreparedBy_Name = {
        status: true,
        message: "This field is required",
      };
    }
    if (!headerObj.WPSH_ApprovedBy_Name || headerObj.WPSH_ApprovedBy_Name === "") {
      isError = true;
      errors.WPSH_ApprovedBy_Name = {
        status: true,
        message: "This field is required",
      };
    }
  }

  return { isError, error: errors };
};

/**
 * Validates date fields against today's date
 * @param {string} dateValue - Date value to validate
 * @param {string} todayDate - Today's date for comparison
 * @returns {Object} Validation result
 */
export const validateDate = (dateValue, todayDate) => {
  if (dateValue > todayDate) {
    return {
      status: true,
      message: VALIDATION_MESSAGES.FUTURE_DATE,
    };
  }
  return { status: false, message: "" };
};

/**
 * Main validation function that combines all validation types
 * @param {Object} config - Validation configuration
 * @param {Object} config.headerObj - Header object to validate
 * @param {Array} config.detailObj - Detail array to validate
 * @param {Object} config.requiredFields - Required fields configuration
 * @param {Array} config.detailConfig - Detail validation configuration
 * @param {Object} config.currentErrors - Current error state
 * @param {string} config.todayDate - Today's date for date validation
 * @returns {Object} Complete validation result
 */
export const validateForm = ({
  headerObj,
  detailObj,
  requiredFields,
  detailConfig,
  currentErrors = {},
  todayDate,
  setError,
}) => {
  // debugger
  let finalErrors = { ...currentErrors };
  let hasErrors = false;
  console.log('validateForm INDIDE - headerObj:',headerObj)
  // Validate required fields
  if (requiredFields && headerObj) {
    const { errors: reqErrors, isError: reqHasError } = validateRequiredFields(
      headerObj,
      requiredFields,
      finalErrors
    );
    finalErrors = reqErrors;
    hasErrors = hasErrors || reqHasError;
  }
  console.log("error1", finalErrors)
  // Validate date fields
  if (todayDate && headerObj?.WPSH_WPS_Date) {
    const dateValidation = validateDate(headerObj.WPSH_WPS_Date, todayDate);
    if (dateValidation.status) {
      hasErrors = true;
      finalErrors.WPSH_WPS_Date = dateValidation;
    }
  }

  // Validate detail arrays - detailConfig contains both single and array field validations  
  if (detailObj && detailConfig) {
    console.log("detailConfig", detailConfig)
    const { errors: detailErrors, isError: detailHasError } =

      validateDetailArrays(detailObj, detailConfig, finalErrors, setError);
    finalErrors = detailErrors;
    hasErrors = hasErrors || detailHasError;
  }
  console.log("error2", finalErrors,hasErrors)
  return {
    errors: finalErrors,
    isError: hasErrors,
  };
};

/**
 * Creates input validation handler for real-time validation
 * @param {Function} setError - Error state setter function
 * @param {Function} validatorFn - Validation function
 * @returns {Function} Input handler function
 */
export const createInputHandler = (setError, validatorFn) => (e, fieldName) => {
  const inputError = validatorFn(e.target.value);
  setError((prevState) => ({
    ...prevState,
    [fieldName]: inputError,
  }));
  return inputError.status;
};

/**
 * Creates error clearing handler
 * @param {Function} setError - Error state setter function
 * @returns {Function} Error clearing handler
 */
export const createErrorClearHandler = (setError) => (fieldName) => {
  setError((prevState) => ({
    ...prevState,
    [fieldName]: { status: false, message: "" },
  }));
};

/**
 * Common first page validation for required fields
 * @param {string} radio - Radio button value
 * @param {Object} fields - Object containing field values
 * @param {Function} seterrobj - Error setter function
 * @returns {boolean} True if validation passes
 */
export const validateFirstPageFields = (radio, fields, seterrobj) => {
  const { ANo, ChemicalAnalysis_TradeName, Classification, FNo, SFA } = fields;

  if (
    radio === "Required" &&
    (ANo === "" ||
      ChemicalAnalysis_TradeName === "" ||
      Classification === "" ||
      FNo === "" ||
      SFA === "" ||
      ANo === undefined ||
      ChemicalAnalysis_TradeName === undefined ||
      Classification === undefined ||
      FNo === undefined ||
      SFA === undefined)
  ) {
    seterrobj((result) => [
      ...result.filter((i) => i.id !== "all"),
      {
        id: "all",
        msg: "All fields are Mandatory",
        className: "border radioInput error",
      },
    ]);
    return false;
  } else {
    console.log("Error flux",fields,radio)
    seterrobj((result) => result.filter((i) => i.id !== "all"));
    return true;
  }
};

/**
 * Utility to check if any errors exist in error object
 * @param {Object} errors - Error object to check
 * @returns {boolean} True if any errors exist
 */
export const hasValidationErrors = (errors) => {
  return Object.values(errors).some((err) => err.status === true);
};

/**
 * Common empty error handler for clearing validation errors
 * @param {Object} err - Error object to process
 * @param {Function} seterr - Error setter function
 * @param {boolean} iserror - Current error state
 * @returns {boolean} Updated error state
 */
export const emptyError = (err, seterr, iserror) => {
  let entries = Object.keys(err);
  entries.forEach((i) => {
    if (err[i].message === process.env.REACT_APP_SPECIAL_CHARACTER_MSG) {
      iserror = true;
      err[i] = {
        status: true,
        message: err[i].message,
      };
    } else if (err[i].length > 0) {
      err[i].forEach((k) => {
        if (k.Value_A != undefined) {
          if (
            k.Value_A[0].message !== process.env.REACT_APP_SPECIAL_CHARACTER_MSG
          ) {
            k.Value_A[0] = {
              status: false,
              message: "",
            };
          } else {
            iserror = true;
          }
        }
        if (k.Value_B != undefined) {
          if (
            k.Value_B[0].message !== process.env.REACT_APP_SPECIAL_CHARACTER_MSG
          ) {
            k.Value_B[0] = {
              status: false,
              message: "",
            };
          } else {
            iserror = true;
          }
        }
      });
    } else {
      err[i] = {
        status: false,
        message: ``,
      };
    }
  });

  seterr((prevState) => ({
    ...prevState,
    ...err,
  }));

  return iserror;
};

/**
 * Generic object creator for test arrays
 * @param {number} count - Number of objects to create
 * @param {Object} template - Template object with field names and default values
 * @returns {Array} Array of objects
 */
export const createTestObjectArray = (count, template) => {
  const result = [];
  for (let i = 1; i <= count; i++) {
    result.push({ ...template });
  }
  return result;
};

/**
 * Common test object templates
 */
export const TEST_TEMPLATES = {
  VISUAL_EXAMINATION: {
    WPQ_VEX_WeldPass: "",
    WPQ_VEX_Acceptance_Criteria: "",
    WPQ_VEX_Observation: "",
    WPQ_VEX_Results: "",
  },
  LIQUID_PENETRANT_EXAMINATION: {
    WPQ_LPEX_WeldPass: "",
    WPQ_LPEX_Acceptance_Criteria: "",
    WPQ_LPEX_Observation: "",
    WPQ_LPEX_Results: "",
  },
  MACRO_EXAMINATION: {
    WPQ_MEX_Tube_No: "",
    WPQ_MEX_LocA: "",
    WPQ_MEX_LocB: "",
    WPQ_MEX_LocC: "",
    WPQ_MEX_LocD: "",
  },
  MINIMUM_LEAK_PATH: {
    WPQ_MLP_Tube_No: "",
    WPQ_MLP_LocA: "",
    WPQ_MLP_LocB: "",
    WPQ_MLP_LocC: "",
    WPQ_MLP_LocD: "",
  },
  OTHER_TEST: {
    WPQ_OT_TypeofTest: "",
    WPQ_OT_Acceptance_Criteria: "",
    WPQ_OT_Observation: "",
    WPQ_OT_Results: "",
  },
  PQR_OTHER_TEST:{
   PQR_OT_TypeofTest : "",
   PQR_OT_Acceptance_Criteria: "",
   PQR_OT_Observation: "",
   PQR_OT_Results: "",
  },
  RADIOGRAPHY_TEST: {
    WPQ_Radio_TubeNo: "",
    WPQ_Radio_Acceptance_Criteria: "",
    WPQ_Radio_Observation: "",
    WPQ_Radio_Results: "",
  },
  MACRO_EXAMINATION_5: {
    WPQ_MEX_Magnification: "",
    WPQ_MEX_Acceptance_Criteria: "",
  },
  MINIMUM_LEAK_PATH_5: {
    WPQ_MLP_Magnification: "",
    WPQ_MLP_Acceptance_Criteria: "",
  },
  PQR_VISUAL_EXAMINATION: {
    PQR_VEX_WeldPass: "",
    PQR_VEX_Acceptance_Criteria: "",
    PQR_VEX_Observation: "",
    PQR_VEX_Results: "",
  },
  PQR_LIQUID_PENETRANT_EXAMINATION: {
    PQR_LPEX_WeldPass: "",
    PQR_LPEX_Acceptance_Criteria: "",
    PQR_LPEX_Observation: "",
    PQR_LPEX_Results: "",
  },
  PQR_MACRO_EXAMINATION: {
    PQR_MEX_Tube_No: "",
    PQR_MEX_LocA: "",
    PQR_MEX_LocB: "",
    PQR_MEX_LocC: "",
    PQR_MEX_LocD: "",
  },
  PQR_MACRO_EXAMINATION_TABLE10: {
    PQR_MEX_Magnification: "",
    PQR_MEX_Acceptance_Criteria: "",
    PQR_MEX_Observation: "",
  },
  PQR_MINIMUM_LEAK_PATH: {
    PQR_MLP_Tube_No: "",
    PQR_MLP_LocA: "",
    PQR_MLP_LocB: "",
    PQR_MLP_LocC: "",
    PQR_MLP_LocD: "",
  },
  PQR_MINIMUM_LEAK_PATH_TABLE5: {
    PQR_MLP_Magnification: "",
    PQR_MLP_Acceptance_Criteria: "",
    PQR_MLP_Observation: "",
  },
  PQR_CERTIFICATION: {
    PQR_Certification_Welder_Name: "",
    PQR_Certification_ID: "",
    PQR_Certification_Tubes_Weld: "",
    PQR_Certification_ConductedBy: "",
    // PQR_Certification_Mechanical_TestsBy: "",
    PQR_Certification_Lab_RefNo: "",
  },
  PQR_TEAR_TEST: {
    PQR_Tear_TubeNo: "",
    PQR_Tear_Observation: "",
    PQR_Tear_Results: "",
  },
  PQR_PULLOUT_TEST: {
    PQR_PullOut_TubeNo: "",
    PQR_PullOut_Location: "",
    PQR_PullOut_Load: "",
    PQR_PullOut_Fracture: "",
  },
  PQR_PULLOUT_TEST_TABLE: {
    PQR_PullOut_Acceptance_Criteria: "",
    PQR_PullOut_Observation: "",
    PQR_PullOut_Results: "",
  },
  PQR_HARDNESS_TEST: {
    PQR_HT_TypeofTest: "",
    PQR_HT_Tube_No: "",
    PQR_HT_BM_Tubesheet: "",
    PQR_HT_HAZ_Tubesheet: "",
    PQR_HT_Weld: "",
    PQR_HT_HAZ_Tube: "",
    PQR_HT_BM_Tube: "",
    PQR_HT_Results: "",
  },
  PQR_TENSILE_TEST: {
    PQR_Tensile_SpecimenNo: "",
    PQR_Tensile_Width: "",
    PQR_Tensile_Thickness: "",
    PQR_Tensile_Area: "",
    PQR_Tensile_UltimateLoad: "",
    PQR_Tensile_UltimateStress: "",
    PQR_Tensile_FailureLocation: "",
  },
  PQR_GUIDED_BEND_TEST: {
    PQR_GB_TypeofTest: "",
    PQR_GB_Acceptance_Criteria: "",
    PQR_GB_Results: "",
  },
  PQR_TOUGHNESS_TEST: {
    PQR_TT_SpecimenNo: "",
    PQR_TT_WeldingProcess: "",
    PQR_TT_NotchLocation: "",
    // PQR_TT_NotchType: "", // Removed - no longer used
    // PQR_TT_SpecimenSize: "", // Moved to header level as PQR_TT_SpecimenSize_Dimension
    PQR_TT_TestTemp: "",
    PQR_TT_ImpactValues: "",
    PQR_TT_Avg: "",
    PQR_TT_LateralExpansion: "",
    PQR_TT_Shear: "",
  },
  PQR_CERTIFICATION_SIMPLE: {
    PQR_Certification_Welder_Name: "",
    PQR_Certification_ID: "",
    PQR_Certification_Process: "",
  },
  PQR_CERTIFICATION_LAB_REF: {
    PQR_Certification_Lab_RefNo: "",
  },
  PQR_GUIDED_BEND_TEST_OVERLAY: {
    PQR_GBV_SpecimenNo: "",
    PQR_GBV_Thickness: "",
    PQR_GBV_BM_WeldOverlay: "",
    PQR_GBV_FormerDiam: "",
    PQR_GBV_BendAngle: "",
    PQR_GBV_Acceptance_Criteria: "",
    PQR_GBV_Remarks: "",
  },
};

/**
 * Create complex table structure for macro examination and minimum leak path
 * @param {number} count - Number of rows to create
 * @param {Object} template - Template object
 * @returns {Array} Array with table structure
 */
export const createComplexTableStructure = (count, template) => {
  const table1 = createTestObjectArray(count, template);
  const table2 = createTestObjectArray(count, template);
  return [{ Table1: table1, Table2: table2 }];
};

/**
 * Create complex PQR table structure with multiple tables
 * @param {number} count - Number of rows to create
 * @param {Object} template - Template object for main tables
 * @param {Object} specialTemplate - Template for special table (Table5/Table10)
 * @param {Array} tableNames - Array of table names
 * @returns {Array} Array with complex table structure
 */
export const createPQRComplexTableStructure = (
  count,
  template,
  specialTemplate,
  tableNames
) => {
  const result = {};
  tableNames.forEach((tableName) => {
    if (tableName === "Table5" || tableName === "Table10") {
      result[tableName] = createTestObjectArray(count, specialTemplate);
    } else {
      result[tableName] = createTestObjectArray(count, template);
    }
  });
  return [result];
};

/**
 * Common fetch utilities for welding data
 */
export const createCommonFetchUtilities = (services) => {
  const {
    getActiveProcess,
    getPNOActive,
    getActiveFno,
    getActiveAno,
    getActiveSfa,
  } = services;

  return {

    fetchAllWeldingData: async (setters) => {
      const {
        setProcessActive,
        setPNoActive,
        setFnoActive,
        setAnoActive,
        setSfaActive,
      } = setters;

      const fetchProcess = async () => {
        if (setProcessActive != undefined) {
          const result = await getActiveProcess();
          const process = [{ wld_code: "NA" }, ...result];
          setProcessActive(process);
        }
      };

      const fetchPNo = async () => {
        const result = await getPNOActive();
        const pno = [{ wld_code: "NA" }, ...result];
        setPNoActive(pno);
      };

      const fetchFno = async () => {
        const result = await getActiveFno();
        setFnoActive(result);
      };

      const fetchAno = async () => {
        const result = await getActiveAno();
        setAnoActive(result);
      };

      const fetchSfa = async () => {
        const result = await getActiveSfa();
        setSfaActive(result);
      };

      await Promise.all([
        fetchProcess(),
        fetchPNo(),
        fetchFno(),
        fetchAno(),
        fetchSfa(),
      ]);
    },
  };
};

/*handle change notes */
const maxNewLines = 13; // Maximum allowed new lines
export const handleChangeNotes = (
  e,
  setIsEdited,
  setIsEditedPage2,
  setEditedPage3,
  setheaderObj,
  setValueNotes
) => {
  setIsEdited(true);
  if (setIsEditedPage2) setIsEditedPage2(true);
  if (setEditedPage3) setEditedPage3(true);
  const { name, value } = e.target;

  // Split the input text into lines based on existing newlines
  const lines = value.split("\n");

  // Limit each line to 100 characters
  const formattedLines = lines.map((line) => {
    // if (line.length > 100) {
    //   // Break the line into chunks of 100 characters
    //   return line.match(/.{1,100}/g).join("\n");
    // }
    return line;
  });

  // Join the lines back together
  let formattedValue = formattedLines.join("\n"); // Use 'let' here instead of 'const'

  // Count the number of new lines
  const newLinesCount = (formattedValue.match(/\n/g) || []).length;

  if (newLinesCount <= maxNewLines) {
    setValueNotes(formattedValue);

    // Update the header object state with the new value
    setheaderObj((prevState) => ({
      ...prevState,
      [name]: formattedValue,
    }));
  } else {
    e.preventDefault(); // Prevent further input if the new line limit is reached
  }
};

/**
 * Flexible handleChangeNotes function with configurable parameters
 * @param {Object} e - Event object
 * @param {Object} config - Configuration object
 * @param {Function} config.setIsEdited - Function to set edited state
 * @param {Function} config.setStateObj - Function to update state (headerObj or detailObj)
 * @param {Function} config.setValueNotes - Function to set value notes
 * @param {number} config.maxNewLines - Maximum allowed new lines (default: 2)
 * @param {number} config.maxLineLength - Maximum characters per line (default: 45)
 * @param {Function} config.actionCheck - Optional function for action-based editing check
 */
export const handleChangeNotesFlexible = (e, config) => {
  const {
    setIsEdited,
    setStateObj,
    setValueNotes,
    maxNewLines = 2,
    maxLineLength = 45,
    actionCheck,
  } = config;

  // Handle action check if provided, otherwise just set edited
  if (actionCheck) {
    actionCheck();
  } else {
    setIsEdited(true);
  }

  const { name, value } = e.target;

  // Split the input text into lines based on existing newlines
  const lines = value.split("\n");

  // Limit each line to specified characters
  const formattedLines = lines.map((line) => {
    if (line.length > maxLineLength) {
      // Break the line into chunks of specified length
      return line.match(new RegExp(`.{1,${maxLineLength}}`, "g")).join("\n");
    }
    return line;
  });

  // Join the lines back together
  let formattedValue = formattedLines.join("\n");

  // Count the number of new lines
  const newLinesCount = (formattedValue.match(/\n/g) || []).length;

  if (newLinesCount <= maxNewLines) {
    setValueNotes(formattedValue);

    // Update the state object with the new value
    setStateObj((prevState) => ({
      ...prevState,
      [name]: formattedValue,
    }));
  } else {
    e.preventDefault(); // Prevent further input if the new line limit is reached
  }
};

export const handleKeyDown = (event, valueNotes) => {
  if (event.key === "Enter") {
    const newLinesCount = (valueNotes.match(/\n/g) || []).length;
    if (newLinesCount >= maxNewLines) {
      event.preventDefault(); // Prevent adding more than 13 new lines
    }
  }
};

/**
 * WPS-specific process validation
 */
export const validateWPSProcesses = (
  detailObj,
  checkProcessAdded,
  checkDuplicateProcess
) => {
  let isError = false;

  // Check if at least one process is added and properly configured
  let norows = checkProcessAdded(detailObj);
  if (norows) {
    // Only set the generic message if no specific error is already set
    if (!detailObj[0].error || detailObj[0].errormessage === "") {
      detailObj[0].errormessage = "At least one Process and Type is required";
      detailObj[0].error = true;
    }
    isError = true;
  }

  // Check for duplicate processes
  if (checkDuplicateProcess(detailObj)) {
    isError = true;
  }

  return { isError };
};

/**
 * WPS first page validation
 */
export const validateWPSFirstPage = (headerObj, detailObj, setError) => {
  let isError = false;

  // Validate required header fields
  const requiredFields = ["WPSH_Project_No", "WPSH_WPS_No", "WPSH_WPS_Revi_No"];

  requiredFields.forEach((field) => {
    if (!headerObj[field] || headerObj[field].trim() === "") {
      setError((prevState) => ({
        ...prevState,
        [field]: { status: true, message: "This field is required" },
      }));
      isError = true;
    }
  });

  return isError;
};

/**
 * Handles navigation between tabs
 * @param {string} tabAction - Type of action ("tab" for tab navigation)
 * @param {number} targetKey - Target tab key
 * @param {number} currentKey - Current active tab key
 * @param {Function} setKey - Function to set active tab
 * @param {number} maxTabs - Maximum number of tabs
 */
export const handleNavigation = (
  tabAction,
  targetKey,
  currentKey,
  setKey,
  maxTabs
) => {
  if (tabAction === "tab" && targetKey >= 1 && targetKey <= maxTabs) {
    setKey(targetKey);
  }
};

/**
 * Executes child component functions
 * @param {Array} refs - Array of child component refs
 * @param {number} currentKey - Current active tab key
 * @param {string} funcName - Function name to execute
 * @returns {Promise} Result from child function
 */
export const executeChildFunction = async (refs, currentKey, funcName) => {
  const currentRef = refs[currentKey - 1];
  if (currentRef?.current?.[funcName]) {
    return await currentRef.current[funcName]();
  }
  return null;
};

/**
 * Shows toast messages based on validation results
 * @param {string} result - Validation result
 * @param {number} currentKey - Current tab key
 * @param {Function} setKey - Function to set active tab
 * @param {number} maxTabs - Maximum number of tabs
 * @param {boolean} allowNext - Whether to allow navigation to next tab
 */
export const showToast = (
  result,
  currentKey,
  setKey,
  maxTabs,
  allowNext = false
) => {
  // This function would typically show toast notifications
  // Implementation depends on the toast library being used
  if (allowNext && result === "Success" && currentKey < maxTabs) {
    setKey(currentKey + 1);
  }
};
